import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Select, Switch, message } from 'antd';
import {
  ArrowLeftOutlined,
  CopyOutlined,
  LogoutOutlined,
  RightOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { settingsEn } from '../../content/settings.en';
import {
  type AppUserSettings,
  getUserPreferences,
  loadLocalUserPreferences,
  patchAppUserSettings,
  flushPreferencesPut,
  hasPendingPreferencesPut,
} from '../../services/userPreferencesSyncService';
import {
  getLastManualSyncAt,
  syncNowFromSettings,
} from '../../services/dataSyncService';
import {
  InboundEmailNotProvisionedError,
  getInboundEmail,
} from '../../services/inboundEmailService';
import { getAppVersionLabel } from '../../utils/appVersion';
import { NATIONALITY_OPTIONS } from '../../utils/nationalityOptions';
import { listTimeZoneOptions } from '../../utils/timeZoneOptions';
import styles from './Settings.module.css';

const formatSyncTime = (iso: string): string =>
  new Date(iso).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });

const Settings: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const [settings, setSettings] = useState<AppUserSettings>(
    () => loadLocalUserPreferences().appUserSettings
  );
  const [loadingRemote, setLoadingRemote] = useState(true);
  const [inboundAddress, setInboundAddress] = useState<string | null>(null);
  const [inboundStatus, setInboundStatus] = useState<
    'loading' | 'ready' | 'missing' | 'error'
  >('loading');
  const [syncing, setSyncing] = useState(false);
  const [lastSyncAt, setLastSyncAt] = useState<string | null>(() =>
    getLastManualSyncAt()
  );
  const [pendingPreferences, setPendingPreferences] = useState(() =>
    hasPendingPreferencesPut()
  );

  const timeZones = useMemo(() => listTimeZoneOptions(), []);
  const appVersion = useMemo(() => getAppVersionLabel(), []);

  const nationalityOptions = useMemo(
    () =>
      NATIONALITY_OPTIONS.map((o) => ({
        value: o.value,
        label: o.iso2 ? `${o.value} (${o.iso2})` : o.value,
      })),
    []
  );

  const timeZoneOptions = useMemo(
    () => timeZones.map((tz) => ({ value: tz, label: tz })),
    [timeZones]
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/signin?redirect=/settings', { replace: true });
      return;
    }
    let cancelled = false;
    setLoadingRemote(true);
    void getUserPreferences()
      .then((payload) => {
        if (!cancelled) setSettings(payload.appUserSettings);
      })
      .catch(() => {
        if (!cancelled) {
          message.warning(settingsEn.loadError);
          setSettings(loadLocalUserPreferences().appUserSettings);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingRemote(false);
      });

    void getInboundEmail()
      .then((info) => {
        if (cancelled) return;
        if (info?.inboundEmailAddress) {
          setInboundAddress(info.inboundEmailAddress);
          setInboundStatus('ready');
        } else {
          setInboundAddress(null);
          setInboundStatus('missing');
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof InboundEmailNotProvisionedError) {
          setInboundStatus('missing');
        } else {
          setInboundStatus('error');
        }
        setInboundAddress(null);
      });

    return () => {
      cancelled = true;
      void flushPreferencesPut().catch(() => {
        // local already saved; ignore flush errors on unmount
      });
    };
  }, [authLoading, isAuthenticated, navigate]);

  const applyPatch = useCallback((patch: Partial<AppUserSettings>) => {
    const next = patchAppUserSettings(patch);
    setSettings(next.appUserSettings);
    setPendingPreferences(hasPendingPreferencesPut());
  }, []);

  const handleSyncNow = async (): Promise<void> => {
    if (!user?.sub || !user.email || syncing) return;
    setSyncing(true);
    try {
      await syncNowFromSettings(user.sub, user.email);
      setLastSyncAt(getLastManualSyncAt());
      setPendingPreferences(hasPendingPreferencesPut());
      message.success(settingsEn.about.syncSuccess);
    } catch {
      message.error(settingsEn.about.syncFailed);
    } finally {
      setSyncing(false);
      setPendingPreferences(hasPendingPreferencesPut());
    }
  };

  const handleCopyInbound = async (): Promise<void> => {
    if (!inboundAddress) return;
    try {
      await navigator.clipboard.writeText(inboundAddress);
      message.success(settingsEn.account.inboundCopied);
    } catch {
      message.error(settingsEn.account.inboundLoadFailed);
    }
  };

  const displayName =
    user?.name ||
    [user?.given_name, user?.family_name].filter(Boolean).join(' ') ||
    null;

  if (authLoading || (!isAuthenticated && loadingRemote)) {
    return (
      <div className={styles.settings}>
        <div className={styles.container}>
          <p className={styles.muted}>{settingsEn.title}…</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.settings}>
      <div className={styles.container}>
        <header className={styles.header}>
          <Button
            type="link"
            className={styles.backLink}
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate(-1)}
          >
            {settingsEn.back}
          </Button>
          <h1 className={styles.title}>{settingsEn.title}</h1>
        </header>

        <section className={styles.section} aria-labelledby="settings-account">
          <h2 id="settings-account" className={styles.sectionTitle}>
            {settingsEn.account.title}
          </h2>
          <div className={styles.accountMeta}>
            <span className={styles.muted}>{settingsEn.account.signedInAs}</span>
            <strong>{user?.email || settingsEn.account.email}</strong>
            {displayName ? (
              <span className={styles.muted}>
                {settingsEn.account.name}: {displayName}
              </span>
            ) : null}
          </div>
          {settings.communityFeaturesEnabled ? (
            <Link className={styles.navLink} to="/settings/profile">
              <span>{settingsEn.account.userProfile}</span>
              <RightOutlined className={styles.chevron} aria-hidden />
            </Link>
          ) : (
            <p className={styles.comingSoon}>
              {settingsEn.account.userProfileNeedsCommunity}
            </p>
          )}
          <div className={styles.inboundBlock}>
            <div className={styles.label}>{settingsEn.account.inboundAddress}</div>
            {inboundStatus === 'loading' ? (
              <p className={styles.muted}>…</p>
            ) : inboundStatus === 'ready' && inboundAddress ? (
              <div className={styles.inboundRow}>
                <code className={styles.inboundAddress}>{inboundAddress}</code>
                <Button
                  type="default"
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={() => void handleCopyInbound()}
                  aria-label={settingsEn.account.inboundCopy}
                >
                  {settingsEn.account.inboundCopy}
                </Button>
              </div>
            ) : (
              <p className={styles.muted}>
                {inboundStatus === 'error'
                  ? settingsEn.account.inboundLoadFailed
                  : settingsEn.account.inboundNotProvisioned}
              </p>
            )}
          </div>
          <Link to="/logout">
            <Button icon={<LogoutOutlined />}>{settingsEn.account.signOut}</Button>
          </Link>
        </section>

        <section className={styles.section} aria-labelledby="settings-display">
          <h2 id="settings-display" className={styles.sectionTitle}>
            {settingsEn.display.title}
          </h2>
          {(
            [
              ['hidePastTrips', settingsEn.display.hidePastTrips],
              ['hidePastCities', settingsEn.display.hidePastCities],
              ['hidePastDays', settingsEn.display.hidePastDays],
              ['expandAllItemsByDefault', settingsEn.display.expandAllItemsByDefault],
              ['collapsePastItems', settingsEn.display.collapsePastItems],
              ['hideCompletedOptions', settingsEn.display.hideCompletedOptions],
              ['showPlanIcons', settingsEn.display.showPlanIcons],
            ] as const
          ).map(([key, label]) => (
            <div className={styles.row} key={key}>
              <span className={styles.label}>{label}</span>
              <Switch
                checked={Boolean(settings[key])}
                disabled={loadingRemote}
                onChange={(checked) => applyPatch({ [key]: checked })}
                aria-label={label}
              />
            </div>
          ))}
          <div className={styles.row}>
            <span className={styles.label}>{settingsEn.display.reservationWarnings}</span>
            <Select
              className={styles.control}
              value={settings.reservationWarningLevel}
              disabled={loadingRemote}
              onChange={(value) => applyPatch({ reservationWarningLevel: value })}
              options={[
                { value: 'required', label: settingsEn.display.reservationRequired },
                { value: 'recommended', label: settingsEn.display.reservationRecommended },
                { value: 'noWarnings', label: settingsEn.display.reservationNone },
              ]}
              aria-label={settingsEn.display.reservationWarnings}
            />
          </div>
          <div className={styles.row}>
            <span className={styles.label}>{settingsEn.display.cruiseMealPlanning}</span>
            <Select
              className={styles.control}
              value={settings.cruiseMealPlanningOption}
              disabled={loadingRemote}
              onChange={(value) => applyPatch({ cruiseMealPlanningOption: value })}
              options={[
                { value: 'oneMeal', label: settingsEn.display.cruiseOneMeal },
                { value: 'threeMeals', label: settingsEn.display.cruiseThreeMeals },
              ]}
              aria-label={settingsEn.display.cruiseMealPlanning}
            />
          </div>
        </section>

        <section className={styles.section} aria-labelledby="settings-user">
          <h2 id="settings-user" className={styles.sectionTitle}>
            {settingsEn.userSettings.title}
          </h2>
          <div className={styles.row}>
            <span className={styles.label}>{settingsEn.userSettings.nationality}</span>
            <Select
              className={styles.control}
              showSearch
              allowClear
              optionFilterProp="label"
              placeholder={settingsEn.userSettings.nationalityPlaceholder}
              value={settings.userNationality ?? undefined}
              disabled={loadingRemote}
              onChange={(value) =>
                applyPatch({ userNationality: value ? String(value) : null })
              }
              options={nationalityOptions}
              aria-label={settingsEn.userSettings.nationality}
            />
          </div>
          <div className={styles.row}>
            <span className={styles.label}>{settingsEn.userSettings.homeTimeZone}</span>
            <Select
              className={styles.control}
              showSearch
              optionFilterProp="label"
              placeholder={settingsEn.userSettings.homeTimeZonePlaceholder}
              value={settings.homeTimeZone}
              disabled={loadingRemote}
              onChange={(value) => applyPatch({ homeTimeZone: value })}
              options={timeZoneOptions}
              aria-label={settingsEn.userSettings.homeTimeZone}
            />
          </div>
          <div className={styles.row}>
            <span className={styles.label}>{settingsEn.userSettings.preferredMapApp}</span>
            <Select
              className={styles.control}
              value={settings.preferredMapApp}
              disabled={loadingRemote}
              onChange={(value) => applyPatch({ preferredMapApp: value })}
              options={[
                { value: 'apple', label: settingsEn.userSettings.mapApple },
                { value: 'google', label: settingsEn.userSettings.mapGoogle },
                { value: 'waze', label: settingsEn.userSettings.mapWaze },
              ]}
              aria-label={settingsEn.userSettings.preferredMapApp}
            />
          </div>
          <Link className={styles.navLink} to="/settings/traveler-preferences">
            <span>{settingsEn.userSettings.travelerPrefs}</span>
            <RightOutlined className={styles.chevron} aria-hidden />
          </Link>
          <Link className={styles.navLink} to="/settings/checklist-template">
            <span>{settingsEn.userSettings.checklistTemplate}</span>
            <RightOutlined className={styles.chevron} aria-hidden />
          </Link>
          <Link className={styles.navLink} to="/settings/allowed-senders">
            <span>{settingsEn.userSettings.allowedSenders}</span>
            <RightOutlined className={styles.chevron} aria-hidden />
          </Link>
        </section>

        <section className={styles.section} aria-labelledby="settings-community">
          <h2 id="settings-community" className={styles.sectionTitle}>
            {settingsEn.community.title}
          </h2>
          <div className={styles.row}>
            <div className={styles.label}>
              <div>{settingsEn.community.enable}</div>
              <p className={styles.hint}>{settingsEn.community.enableHint}</p>
            </div>
            <Switch
              checked={settings.communityFeaturesEnabled}
              disabled={loadingRemote}
              onChange={(checked) => applyPatch({ communityFeaturesEnabled: checked })}
              aria-label={settingsEn.community.enable}
            />
          </div>
        </section>

        <section className={styles.section} aria-labelledby="settings-about">
          <h2 id="settings-about" className={styles.sectionTitle}>
            {settingsEn.about.title}
          </h2>
          <div className={styles.row}>
            <span className={styles.label}>{settingsEn.about.version}</span>
            <span className={styles.muted}>{appVersion}</span>
          </div>
          {isAuthenticated && user?.sub && user.email ? (
            <>
              <div className={styles.syncActions}>
                <Button
                  icon={<SyncOutlined spin={syncing} />}
                  loading={syncing}
                  onClick={() => void handleSyncNow()}
                  aria-label={settingsEn.about.syncNow}
                >
                  {syncing ? settingsEn.about.syncing : settingsEn.about.syncNow}
                </Button>
              </div>
              {lastSyncAt ? (
                <div className={styles.row}>
                  <span className={styles.label}>{settingsEn.about.lastSync}</span>
                  <span className={styles.muted}>{formatSyncTime(lastSyncAt)}</span>
                </div>
              ) : null}
              {pendingPreferences ? (
                <p className={styles.pendingHint} role="status">
                  {settingsEn.about.pendingPreferences}
                </p>
              ) : null}
            </>
          ) : (
            <p className={styles.muted}>{settingsEn.about.signInToSync}</p>
          )}
        </section>

        <section className={styles.section} aria-labelledby="settings-legal">
          <h2 id="settings-legal" className={styles.sectionTitle}>
            {settingsEn.legal.title}
          </h2>
          <Link className={styles.navLink} to="/privacy">
            <span>{settingsEn.legal.privacy}</span>
            <RightOutlined className={styles.chevron} aria-hidden />
          </Link>
          <Link className={styles.navLink} to="/terms">
            <span>{settingsEn.legal.terms}</span>
            <RightOutlined className={styles.chevron} aria-hidden />
          </Link>
          <div className={styles.attributions}>
            <div className={styles.label}>{settingsEn.legal.attributions}</div>
            <a
              className={styles.externalLink}
              href={settingsEn.legal.openMeteoUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              {settingsEn.legal.openMeteo}
            </a>
            <a
              className={styles.externalLink}
              href={settingsEn.legal.googleMapsUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              {settingsEn.legal.googleMaps}
            </a>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Settings;
